import { Interval } from './interval';

/**
 * This class extends the Interval class to calculate the rate of events per interval.
 * It takes into account the total number of events in the interval and the number of events in the previous interval.
 * It also takes into account the default intervals.
 */
export class IntervalRate<T extends string> extends Interval<T> {
  private readonly multiplier: number;

  constructor(
    protected override defaultIntervals: Record<T, number | undefined>,
    private readonly precision = 4,
  ) {
    super(defaultIntervals);
    this.multiplier = Math.pow(10, this.precision);
  }

  override getAggregated(): Record<T, number | undefined> {
    const intervals = super.getAggregated();
    const rates: Record<T, number | undefined> = {} as Record<
      T,
      number | undefined
    >;

    if (this.isEmpty(intervals)) {
      return { ...this.defaultIntervals };
    }

    const total = (Object.values(intervals) as number[]).reduce<number>(
      (acc, interval) => acc + (interval || 0),
      0,
    );

    if (total === 0) {
      return { ...this.defaultIntervals };
    }

    for (const key in intervals) {
      rates[key] = intervals[key]
        ? Math.round((intervals[key]! / total) * this.multiplier) /
          this.multiplier
        : 0;
    }

    return rates;
  }

  private isEmpty(obj: Record<T, number | undefined>): boolean {
    return Object.values(obj).every((value) => value === undefined);
  }
}
